import { BOSS_LEVELS, DEFAULT_RULES } from '@bioforge/game-core';
import {
  BOSS_IDS,
  type BossId,
  type CatalogEntry,
  type CatalogKind,
  type ContentResponse,
  type SceneView,
} from '@bioforge/protocol';
import { BOSS_TITLES, VESSEL_NAMES } from '../labels.js';
import { tr } from '../story/story.js';
import { capsulesWord } from './battle.js';

/** Разделы энциклопедии (макет «Энциклопедия · микробы и боссы» и остальной каталог). */
export type EncyclopediaTab = 'microbes' | 'bosses' | 'tricks' | 'care' | 'lab' | 'archive';

export const TABS: readonly { readonly id: EncyclopediaTab; readonly label: string }[] = [
  { id: 'microbes', label: 'Микробы' },
  { id: 'bosses', label: 'Боссы' },
  { id: 'tricks', label: 'Проделки' },
  { id: 'care', label: 'Лечение' },
  { id: 'lab', label: 'Лаборатория' },
  { id: 'archive', label: 'Архив' },
];

const TAB_KINDS: Readonly<Record<Exclude<EncyclopediaTab, 'archive'>, readonly CatalogKind[]>> = {
  microbes: ['microbe', 'modifier'],
  bosses: ['boss'],
  tricks: ['boss-ability', 'effect'],
  care: ['ability', 'attack', 'role'],
  lab: ['rule', 'vessel'],
};

/** Надпись над названием статьи. */
export const KIND_KICKER: Readonly<Record<CatalogKind, string>> = {
  rule: 'Правило',
  microbe: 'Микроб',
  modifier: 'Особенность',
  vessel: 'Сосуд',
  boss: 'Босс',
  'boss-ability': 'Способность босса',
  ability: 'Способность',
  attack: 'Атака',
  role: 'Роль',
  effect: 'Эффект',
};

const SCENE_KIND: Readonly<Record<SceneView['kind'], string>> = {
  comic: 'Сцена главы',
  'boss-intro': 'Вступление босса',
  victory: 'Победа',
};

/** Строка списка: статья каталога или сцена архива. */
export interface ListItem {
  readonly id: string;
  /** Закрыта, пока игрок её не встретил. */
  readonly locked: boolean;
  readonly name: string;
  readonly sub: string;
  /** Ключ рисунка (`catalog-<id>`, `boss-locked`, `scene-<id>`). */
  readonly art: string;
}

export interface Seen {
  has(kind: 'intro' | 'scene', id: string): boolean;
}

const isBoss = (id: string): id is BossId => (BOSS_IDS as readonly string[]).includes(id);

export function bossOf(entry: Pick<CatalogEntry, 'id' | 'kind'>): BossId | null {
  const tail = entry.id.replace(/^boss-/, '');
  return entry.kind === 'boss' && isBoss(tail) ? tail : null;
}

/** Глава, где игрок встречает босса. */
function bossChapter(content: ContentResponse, id: BossId): number | null {
  return content.chapters.find((c) => c.bossId === id)?.number ?? null;
}

function entrySub(content: ContentResponse, entry: CatalogEntry): string {
  const boss = bossOf(entry);
  if (boss) {
    const chapter = bossChapter(content, boss);
    const vessel = VESSEL_NAMES[BOSS_LEVELS[boss].vessel];
    return chapter === null ? vessel : `Глава ${chapter} · ${vessel}`;
  }
  const microbe = /^microbe-(\d+)$/.exec(entry.id);
  if (microbe) {
    return DEFAULT_RULES.bacteriaColors.includes(Number(microbe[1])) ? 'Бактерия' : 'Вирус';
  }
  return KIND_KICKER[entry.kind];
}

export function listItems(content: ContentResponse, seen: Seen, tab: EncyclopediaTab): ListItem[] {
  if (tab === 'archive') {
    return content.scenes.map((scene) => {
      const locked = !seen.has('scene', scene.id);
      return {
        id: scene.id,
        locked,
        name: locked ? '???' : tr(scene.title),
        sub: locked ? 'Ещё не открыта' : SCENE_KIND[scene.kind],
        art: `scene-${scene.id}`,
      };
    });
  }
  const kinds = TAB_KINDS[tab];
  return content.catalog
    .filter((entry) => kinds.includes(entry.kind))
    .sort((a, b) => kinds.indexOf(a.kind) - kinds.indexOf(b.kind))
    .map((entry) => {
      const locked = !seen.has('intro', entry.id);
      const sub = entrySub(content, entry);
      return {
        id: entry.id,
        locked,
        name: locked ? '???' : tr(entry.title),
        sub: locked ? `${sub} · ещё не встречено` : sub,
        art: locked && entry.kind === 'boss' ? 'boss-locked' : `catalog-${entry.id}`,
      };
    });
}

/** Способность босса в статье: когда срабатывает и что делает. */
export interface BossAbilityLine {
  readonly kind: string;
  readonly name: string;
  readonly text: string;
}

export interface BossFacts {
  readonly hp: number;
  readonly tags: readonly string[];
  readonly abilities: readonly BossAbilityLine[];
  /** Первая реплика босса во вступлении (если она есть в сюжете). */
  readonly quote: string | null;
}

const PLACEMENT: Readonly<Record<'vessel' | 'outside' | 'hybrid', string>> = {
  vessel: 'В сосуде',
  outside: 'За стеклом',
  hybrid: 'Щупальца в сосуде',
};

export function bossFacts(content: ContentResponse, id: BossId): BossFacts {
  const config = BOSS_LEVELS[id].boss;
  const abilities = config.abilities.map((spec) => {
    const when =
      spec.every !== null
        ? spec.every === 1
          ? 'Каждую капсулу'
          : `Каждые ${spec.every} ${capsulesWord(spec.every)}`
        : `При здоровье ${spec.belowHp ?? 0} и ниже`;
    const what = content.catalog.find((e) => e.id === `boss-${spec.kind}`);
    return { kind: spec.kind, name: spec.name, text: what ? `${when}. ${tr(what.what)}` : when };
  });
  const chapter = content.chapters.find((c) => c.bossId === id);
  const bossLevel = chapter?.levels.find((l) => l.kind === 'boss');
  const intro = content.scenes.find((s) => s.id === bossLevel?.sceneBefore);
  const line = intro?.frames.flatMap((f) => f.lines).find((l) => l.speaker === id);
  return {
    hp: config.hp,
    tags: [BOSS_TITLES[id], PLACEMENT[config.placement]],
    abilities,
    quote: line ? tr(line.text) : null,
  };
}

/** Чем лечить микроба в игре: что помогает и что нет. */
export function microbeCare(color: number): { good: string[]; bad: string[] } {
  return DEFAULT_RULES.bacteriaColors.includes(color)
    ? { good: ['Антибиотик', 'Криотерапия', 'Цепочки'], bad: [] }
    : { good: ['Криотерапия', 'Цепочки'], bad: ['Антибиотик — на вирусы не действует'] };
}

/** Сколько статей и сцен уже открыто. */
export function openedCount(
  content: ContentResponse,
  seen: Seen,
): { opened: number; total: number } {
  const opened =
    content.catalog.filter((e) => seen.has('intro', e.id)).length +
    content.scenes.filter((s) => seen.has('scene', s.id)).length;
  return { opened, total: content.catalog.length + content.scenes.length };
}
